import { Component, Input, forwardRef, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

import { of, Subscription, catchError, map } from 'rxjs';

import { AbstractFormFieldComponent } from '../../FormField/abstract-form-field.component';
import { ChartColorCheckboxesFieldModel } from '../../../models/chart-color-checkboxes-field.model';
import { ColorCheckboxesFieldComponent } from '../ColorCheckboxesField/color-checkboxes-field.component';
import { DataConnectorTypesEnum } from '../../../../../api/externalData/enums/data-connector-types.enum';
import { GoogleAnalyticsReportTypesEnum } from '../../../../../api/externalData/enums/google-analytics-report-types.enum';
import { ReportSeriesModel } from '../../../../Reports/models/report-series.model';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '../../../../../Core/Users/data/user-app-preferences-default';


// frequently, collections of checkbox options come with an associated color which you can pick
// this component does that job by putting a color swatch below every checkbox
@Component({
    selector: 'agpr-chart-color-checkboxes-field',
    templateUrl: '../ColorCheckboxesField/color-checkboxes-field.component.html',
    styleUrls: ['../ColorCheckboxesField/color-checkboxes-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => ChartColorCheckboxesFieldComponent)
        }
    ],
    standalone: false
})
export class ChartColorCheckboxesFieldComponent extends ColorCheckboxesFieldComponent implements OnInit, OnDestroy, OnChanges {
    @Input() declare field: ChartColorCheckboxesFieldModel;

    valueMap: { [key: string]: boolean } = {};

    private subscriptions: Subscription[] = [];

    constructor(private userPreferencesService: UserPreferencesService) {
        super();
    }

    ngOnInit() {
        if (!this.block.displayElement.styles.colorMap) {
            this.block.displayElement.styles.colorMap = {};
        }

        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
            ).subscribe(chartColors => {
                this.field.options.forEach(option => {
                    if (!this.block.displayElement.styles.colorMap[option.key]) {
                        this.block.displayElement.styles.colorMap[option.key] =
                            chartColors[option.userPreferenceColor.colorType][option.userPreferenceColor.typeProperty];
                    }
                });
                this.filteredOptions = this.field.options;
                this.updateValueMap();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    // Since the checkbox options are passed in as a fixed array as configuration,
    // at run time we can only filter the options on the attribute grabbed from the displayElement
    ngOnChanges(changes: SimpleChanges): void {
        if (this.field.property === 'referralTypes') {
            const externalDataType = (this.block.displayElement['externalDataSeries']?.[0] as ReportSeriesModel)?.externalData?.externalDataType;
            if (externalDataType) {
                let filteredOptionType = null;
                if (externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS) {
                    filteredOptionType = GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS;
                } else if (externalDataType === DataConnectorTypesEnum.GOOGLE_ANALYTICS_4) {
                    filteredOptionType = GoogleAnalyticsReportTypesEnum.UNIQUE_PAGEVIEWS;
                }
                this.filteredOptions = this.field.options.filter(option => option.key !== filteredOptionType);
                this.updateValueMap();
            }
        }
    }

    handleChanges(event?: any): void {
        this.updateValueMap();
        this.changes.emit(event || this.value);
    }

    updateValueMap(): void {
        this.valueMap = {};

        if (!this.value) {
            this.value = [];
        }

        // build an object that lets us see what checkboxes are enabled in the template
        (<[]>this.value).forEach(value => {
            this.valueMap[value] = true;
        });
    }

    // |key| should usually be an id/enum value identifying the appropriate element
    handleColorChange(colorHexString: string, key: string): void {
        this.block.displayElement.styles.colorMap[key] = colorHexString;
        this.handleChanges();
    }
}
